import React from 'react';

import { Dialog, DialogContent, DialogDescription, DialogTitle } from 'components/ui/dialog';
import { Star } from 'lucide-react';
import { useGetReviewDetail } from 'shared/api';
import { occupationMap } from 'shared/constants/occupation';
import { MainReviewsDto } from 'shared/model';
import { CopyGuard, WatermarkedImage } from 'shared/ui';

export function ReviewDetailDialog({
  open,
  onOpenChange,
  reviewMemberInfo,
  contents_raw,
  rating,
  id,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  reviewMemberInfo: MainReviewsDto['reviewMainInfos'][number]['reviewMemberInfo'];
  contents_raw: MainReviewsDto['reviewMainInfos'][number]['contents_raw'];
  rating: MainReviewsDto['reviewMainInfos'][number]['rating'];
  id: MainReviewsDto['reviewMainInfos'][number]['reviewId'];
}) {
  const { firstName, nationality, occuptionType } = reviewMemberInfo;

  const { data } = useGetReviewDetail(id);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="!w-[948px] !max-w-[948px] rounded-[32px] border-0 bg-white p-[58px_64px] shadow-[4px_4px_39px_rgba(0,0,0,0.07)]">
        {/* A11y: Title/Description for aria associations */}
        <DialogTitle className="sr-only">Review Detail</DialogTitle>
        <DialogDescription className="sr-only">Full review content and photos</DialogDescription>

        <CopyGuard>
          <div className="flex flex-col gap-[28px]">
            <div className="flex flex-col gap-[6px]">
              <div className="flex flex-col gap-[2px]">
                <div className="flex items-center gap-[12px]">
                  <span className="text-[24px] font-semibold leading-[1.5] tracking-[-0.022em] text-[#222222]">
                    {firstName}
                  </span>

                  <div className="flex items-center gap-[1px]">
                    {Array.from({ length: rating }).map((_, i) => (
                      <Star key={i} className="h-[27px] w-[27px] fill-[#FBE34C] text-[#FBE34C]" />
                    ))}
                  </div>
                </div>
                <div className="flex flex-col gap-[4px]">
                  <span className="text-[18px] font-medium leading-[1.5] tracking-[-0.022em] text-[#4F4F4F]">
                    {nationality}, {occupationMap[occuptionType]}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-x-2 overflow-hidden">
              {(data?.reviewImageUrls || []).slice(0, 3).map((src: string, idx: number) => (
                <WatermarkedImage
                  key={idx}
                  src={src}
                  alt="review"
                  className="h-[108px] w-[108px] rounded-[8px] object-cover"
                />
              ))}
            </div>

            <p className="break-all text-[18px] font-normal leading-[1.6] tracking-[-0.022em] text-[#222222]">
              {contents_raw}
            </p>
          </div>
        </CopyGuard>
      </DialogContent>
    </Dialog>
  );
}
